import { FC } from "react";
import { SectionDivider } from "../../../common/components/SectionDivider";
import { PdfLayout } from "../../../common/components/PdfLayout";
import { IncomeStatement, FinancialStatements, Ratio } from "../../../types/FinanceTypes";
import { OrderForm } from "../../../types/OrderFormTypes";
import { Competitors } from "./components/Competitors";
import { FinancialNews, FinancialNewsProps } from "./components/FinancialNews";
import { InsightsCard } from "./components/InsightsCard";
import { SupplierBase } from "./components/SupplierBase";
import { SupplierOverviewLeftPanel } from "./SupplierOverviewLeftPanel";
import { CompetitorComarisions } from "./components/CompetitorComparisons";
import { Discounts } from "./components/Discounts";
import { DiscountTrends } from "./components/DiscountTrends";
import { EmergingAlternatives } from "./components/EmergingAlternatives";
import { EmergingTechnologies } from "./components/EmergingTechnologies";
import { HiddenCosts } from "./components/HiddenCosts";
import { InHouseFeasibilities } from "./components/InHouseFeasibility";
import { LegalIssues } from "./components/LegalIssues";
import { LeverageSummary } from "./components/LeverageSummary";
import { MarketCharts } from "./components/MarketCharts";
import { NegotiatedTerms } from "./components/NegotiatedTerms";
import { PricingModels } from "./components/PricingModels";
import { QualityCostTradeOffs } from "./components/QualityCostTradeOffs";
import { FinancialStatements as FinancialStatementsComponent } from "./components/FinancialStatements";
import { RetainedEarningSchedule } from "./components/RetainedEarningSchedule";
import { PPESchedule } from "./components/PPESchedule";
import { FinancialCharts } from "./components/FinancialCharts";
import {
    Period,
    SupplierFullCompetitor,
    SupplierInsight,
    SupplierWithEmployee,
} from "../../../types/SupplierTypes";
import { SupplierRatiosPanel } from "./SupplierRatiosPanel";
import { MarketTrendsPanel } from "./MarketTrendsPanel";
import { BenchmarkingPanel } from "./BenchmarkingPanel";

interface PdfPanelProps {
    profile?: SupplierWithEmployee;
    employeeCount?: number;
    employeeChange?: number;
    employeeChangePercent?: number;
    period?: Period;
    incomes?: IncomeStatement[];
    orderForm?: OrderForm;
    competitors?: SupplierFullCompetitor[];
    insights?: SupplierInsight[];
    newsProps?: FinancialNewsProps;
    financialStatements?: FinancialStatements;
    ratios?: Ratio[];
    orgName?: string;
}

export const PdfPanel: FC<PdfPanelProps> = ({
    profile,
    employeeCount,
    employeeChange,
    employeeChangePercent,
    period,
    incomes,
    orderForm,
    competitors,
    insights,
    newsProps,
    financialStatements,
    ratios,
    orgName,
}) => {
    const withFinancials = [
        <>
            <SupplierBase
                {...profile}
                currentEmployee={employeeCount}
                employeeDifference={employeeChange}
                showPdfButton={false}
            />
            <LeverageSummary
                leverage={profile?.analysis?.leverage}
                style={{ marginBlock: 16 }}
                showButtons={false}
                defaultActive
            />
            <SectionDivider>Overview</SectionDivider>
            <SupplierOverviewLeftPanel
                profile={profile}
                period={period}
                incomes={incomes}
                orderForm={orderForm}
            />
        </>,
        <>
            <Competitors data={competitors} />
            <EmergingTechnologies
                style={{ marginBlock: 16 }}
                replacements={profile?.analysis?.replacements}
                innovations={profile?.analysis?.innovations}
                newBusinessModels={profile?.analysis?.newBusinessModels}
            />
            <InsightsCard insights={insights} orgName={orgName} />
        </>,
        <>
            <FinancialNews
                {...newsProps}
                showLoadButton={false}
                limit={4}
                style={{ marginTop: 16 }}
            />
            <SectionDivider style={{ marginTop: 16 }}>Market trends</SectionDivider>
            <MarketCharts
                industryGrowth={profile?.analysis?.industryGrowth}
                supplyAndDemand={profile?.analysis?.supplyAndDemand}
                economicFactors={profile?.analysis?.economicFactors}
                costAndMargins={profile?.analysis?.costAndMargins}
                reputation={profile?.analysis?.reputation}
                staff={profile?.analysis?.staff}
                incomes={financialStatements?.incomes}
                employees={employeeCount}
                employeeIncrease={employeeChangePercent}
                style={{ marginTop: 16 }}
            />
        </>,
        <>
            <DiscountTrends data={profile?.analysis?.discountingTrends} style={{ marginTop: 16 }} />
            <Discounts data={profile?.analysis?.discounts} style={{ marginTop: 16 }} />
            <LegalIssues data={profile?.analysis?.issues} style={{ marginTop: 16 }} />
            <SectionDivider style={{ marginTop: 16 }}>Benchmarking</SectionDivider>
            <PricingModels data={profile?.analysis?.pricingModels} />
            <NegotiatedTerms data={profile?.analysis?.negotiatedTerms} style={{ marginTop: 16 }} />
            <HiddenCosts data={profile?.analysis?.hiddenCosts} style={{ marginTop: 16 }} />
            <EmergingAlternatives
                data={profile?.analysis?.emergingAlternatives}
                style={{ marginTop: 16 }}
            />
        </>,
        <>
            <InHouseFeasibilities
                data={profile?.analysis?.inHouseFeasibilities}
                style={{ marginTop: 16 }}
            />
            <CompetitorComarisions
                data={profile?.analysis?.competitorComparisons}
                style={{ marginTop: 16 }}
            />
            <QualityCostTradeOffs
                data={profile?.analysis?.qualityCostTradeOffs}
                style={{ marginTop: 16 }}
            />
            <SectionDivider style={{ marginTop: 16 }}>Financial statements</SectionDivider>
            <FinancialStatementsComponent
                incomes={financialStatements?.incomes}
                balanceSheets={financialStatements?.balanceSheets}
                cashFlows={financialStatements?.cashFlows}
                showDownload={false}
            />
        </>,
        <>
            <RetainedEarningSchedule
                balanceSheets={financialStatements?.balanceSheets?.annually}
                cashFlows={financialStatements?.cashFlows?.annually}
                style={{ marginTop: 16 }}
            />
            <PPESchedule
                balanceSheets={financialStatements?.balanceSheets?.annually}
                cashFlows={financialStatements?.cashFlows?.annually}
                style={{ marginTop: 16 }}
            />
            <SectionDivider style={{ marginTop: 16 }}>Financial health</SectionDivider>
            <SupplierRatiosPanel ratios={ratios} />
        </>,
        <>
            <FinancialCharts period={period} incomes={incomes} style={{ marginTop: 16 }} />
        </>,
    ];

    const withoutFinancials = [
        <>
            <SupplierBase
                {...profile}
                currentEmployee={employeeCount}
                employeeDifference={employeeChange}
                showPdfButton={false}
            />
            <LeverageSummary
                leverage={profile?.analysis?.leverage}
                style={{ marginBlock: 16 }}
                showButtons={false}
                defaultActive
            />
            <SectionDivider>Overview</SectionDivider>
            <SupplierOverviewLeftPanel
                profile={profile}
                period={period}
                incomes={incomes}
                orderForm={orderForm}
            />
        </>,
        <>
            <Competitors data={competitors} />
            <EmergingTechnologies
                style={{ marginBlock: 16 }}
                replacements={profile?.analysis?.replacements}
                innovations={profile?.analysis?.innovations}
                newBusinessModels={profile?.analysis?.newBusinessModels}
            />
            <InsightsCard insights={insights} orgName={orgName} />
        </>,
        ...(profile?.analysis
            ? [
                  <>
                      <SectionDivider style={{ marginTop: 16 }}>Market trends</SectionDivider>
                      <MarketTrendsPanel
                          analysis={profile?.analysis}
                          incomes={financialStatements?.incomes}
                          employees={employeeCount}
                          employeeIncrease={employeeChangePercent}
                      />
                  </>,
                  <>
                      <SectionDivider style={{ marginTop: 16 }}>Benchmarking</SectionDivider>
                      <BenchmarkingPanel analysis={profile?.analysis} />
                  </>,
              ]
            : []),
    ];

    const dispalyPdfs = incomes?.length ? withFinancials : withoutFinancials;

    return (
        <>
            {dispalyPdfs.map((ele, index) => (
                <PdfLayout key={index} title="Buyer brief" company={orgName} page={index + 1}>
                    {ele}
                </PdfLayout>
            ))}
        </>
    );
};
